import { useState, useEffect } from "react";
import { getUsers } from "../services/userService";
import useDebounce from "./useDebounce";

interface RawUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  [key: string]: unknown;
}

export interface UserOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  primaryContact: string;
}

const PAGE_SIZE = 10;

function normalizeUserOption(u: RawUser): UserOption {
  return {
    id: String(u.id),
    firstName: String(u.firstName ?? ""),
    lastName: String(u.lastName ?? ""),
    email: String(u.email ?? ""),
    phone: String(u.phone ?? ""),
    address: String(u.address ?? ""),
    primaryContact: String(u.primaryContact ?? ""),
  };
}

export function useUsersByRole(role: string) {
  const [options, setOptions] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  const [loadParams, setLoadParams] = useState({ search: "", page: 1 });

  const debouncedSearch = useDebounce(search, 400);

  useEffect(() => {
    setLoadParams({ search: debouncedSearch, page: 1 });
  }, [debouncedSearch]);

  useEffect(() => {
    if (!role) return;
    let cancelled = false;

    const fetchUsers = async () => {
      setLoading(true);
      const { search: s, page } = loadParams;
      const params: Record<string, unknown> = { page, limit: PAGE_SIZE, role };
      if (s.trim()) params.q = s.trim();

      const { data, error } = (await getUsers(params)) as {
        data: { data?: RawUser[]; pagination?: { total: number } } | null;
        error: unknown;
      };

      if (cancelled) return;

      if (!error) {
        const list: RawUser[] = Array.isArray(data?.data) ? data!.data! : [];
        const normalized = list.map(normalizeUserOption);
        const total = data?.pagination?.total ?? 0;
        setOptions((prev) => {
          const updated = page === 1 ? normalized : [...prev, ...normalized];
          setHasMore(updated.length < total);
          return updated;
        });
      }
      setLoading(false);
    };

    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [role, loadParams]);

  return {
    options,
    loading,
    hasMore,
    onSearch: (value: string) => setSearch(value),
    onLoadMore: () => setLoadParams((prev) => ({ ...prev, page: prev.page + 1 })),
  };
}
